import type { User as FirebaseUser } from 'firebase/auth';
import { onAuthStateChanged, signInWithPopup, signOut as firebaseSignOut, GoogleAuthProvider } from 'firebase/auth';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { firebaseAuth } from '@/lib/firebase';

// ── Types ────────────────────────────────────────────

export interface PlatformUser {
  id: string;
  email: string;
  name: string | null;
  role: 'ADMIN' | 'OPERATOR' | 'VIEWER' | 'DEVELOPER';
  isSuperAdminTenant: boolean;
  tenant: {
    id: string;
    name: string;
    slug: string;
    plan: string;
    status: string;
  };
}

interface AuthState {
  firebaseUser:  FirebaseUser | null;
  platformUser:  PlatformUser | null;
  idToken:       string | null;
  loading:       boolean;
  error:         string | null;
}

interface AuthContextValue extends AuthState {
  signInWithGoogle: () => Promise<void>;
  signOut:          () => Promise<void>;
  refreshToken:     () => Promise<string | null>;
}

// ── Context ──────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

// ── Provider ─────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    firebaseUser: null,
    platformUser: null,
    idToken:      null,
    loading:      true,
    error:        null,
  });
  // Prevents onAuthStateChanged(null) from clearing our error when WE triggered the sign-out
  const blockNextNullCallbackRef = useRef(false);

  // Fetch the platform user from the API
  async function fetchPlatformUser(token: string): Promise<PlatformUser | null> {
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/v1/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'x-enska-portal': 'admin',
        },
      });
      if (!res.ok) return null;
      return res.json();
    } catch {
      return null;
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, async (fbUser) => {
      if (!fbUser) {
        if (blockNextNullCallbackRef.current) {
          blockNextNullCallbackRef.current = false;
          return; // Error state already set — don't clear it
        }
        setState({ firebaseUser: null, platformUser: null, idToken: null, loading: false, error: null });
        return;
      }

      try {
        const token = await fbUser.getIdToken();
        const platformUser = await fetchPlatformUser(token);

        // Block access — no platform account for this Google identity
        if (!platformUser) {
          blockNextNullCallbackRef.current = true;
          await firebaseSignOut(firebaseAuth);
          setState({ firebaseUser: null, platformUser: null, idToken: null, loading: false,
            error: 'No account found for this email. Contact your administrator.' });
          return;
        }

        setState({ firebaseUser: fbUser, platformUser, idToken: token, loading: false, error: null });
      } catch (err) {
        setState((s) => ({ ...s, loading: false, error: String(err) }));
      }
    });

    return unsubscribe;
  }, []);

  async function signInWithGoogle() {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(firebaseAuth, provider);
      // onAuthStateChanged will handle state update
    } catch {
      setState((s) => ({ ...s, loading: false, error: 'Sign-in failed. Please try again.' }));
    }
  }

  async function signOut() {
    await firebaseSignOut(firebaseAuth);
    setState({ firebaseUser: null, platformUser: null, idToken: null, loading: false, error: null });
  }

  async function refreshToken(): Promise<string | null> {
    if (!state.firebaseUser) return null;
    const token = await state.firebaseUser.getIdToken(true);
    setState((s) => ({ ...s, idToken: token }));
    return token;
  }

  return (
    <AuthContext.Provider value={{ ...state, signInWithGoogle, signOut, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ─────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
